class DetailsModal extends HTMLElement {
  constructor() {
    super();
    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');

    if (!this.detailsContainer || !this.summaryToggle) return;

    this.closeButton = this.querySelector('button[type="button"]');
    this.onKeyUpEscapeHandler = this.onKeyUpEscape.bind(this);

    // Expose the disclosure as a button with an accurate expanded state so
    // screen readers announce open/closed and the trigger can be styled.
    this.summaryToggle.setAttribute('role', 'button');
    this.summaryToggle.setAttribute('aria-expanded', 'false');
    if (this.detailsContainer.id) this.summaryToggle.setAttribute('aria-controls', this.detailsContainer.id);

    this.summaryToggle.addEventListener('click', this.onSummaryClick.bind(this));
    this.closeButton?.addEventListener('click', this.onCloseButtonClick.bind(this));

    // Delegated repeated-trigger fallback: while open, the absolutely
    // positioned panel can intercept a second tap on the trigger area before it
    // reaches the summary. Catching that click at the container keeps the
    // repeated-toggle close working even if stacking order is disturbed.
    this.detailsContainer.addEventListener('click', this.onDetailsClick.bind(this));

    // Escape is handled at the document level so it works regardless of which
    // element inside the overlay currently holds focus. The handler is a no-op
    // while the modal is closed, so this is safe to keep attached for life.
    document.addEventListener('keyup', this.onKeyUpEscapeHandler);
  }

  isOpen() {
    return this.detailsContainer?.hasAttribute('open') ?? false;
  }

  onKeyUpEscape(event) {
    if (!isEscapeKey(event)) return;
    if (!this.isOpen()) return;
    this.close();
  }

  onCloseButtonClick(event) {
    event.preventDefault();
    this.close();
  }

  onSummaryClick(event) {
    event.preventDefault();
    // The trigger both opens and closes, matching native <summary> behaviour.
    this.isOpen() ? this.close() : this.open(event);
  }

  // Repeated-trigger close fallback. When the open panel covers the header, a
  // second tap on the trigger can land on the non-interactive panel instead of
  // the summary. Map any click inside the trigger's rectangle that did not hit
  // a real control (input, button, link, option) to `close()`, so the same
  // control always toggles the overlay shut. Real controls are ignored so the
  // search field and close button keep working normally.
  onDetailsClick(event) {
    if (!this.isOpen()) return;
    // A click that starts inside the summary is already toggled by
    // onSummaryClick. Without this guard the opening click bubbles up to the
    // container, which then sees the modal open and its coordinates inside the
    // trigger rect, and closes it again in the same gesture — so the icon
    // appears dead on press. Only panel-over-trigger clicks fall through.
    if (event.target.closest('summary')) return;
    if (event.target.closest('input, button, a, [role="option"]')) return;

    const rect = this.summaryToggle.getBoundingClientRect();
    const withinX = event.clientX >= rect.left && event.clientX <= rect.right;
    const withinY = event.clientY >= rect.top && event.clientY <= rect.bottom;
    if (!withinX || !withinY) return;

    event.preventDefault();
    this.close();
  }

  onBodyClick(event) {
    if (!this.contains(event.target) || event.target.classList.contains('modal-overlay')) {
      // Backdrop / outside click closes but leaves focus where the shopper
      // clicked rather than snapping it back to the search trigger.
      this.close(false);
    }
  }

  open(event) {
    if (this.isOpen()) return;

    // Remember the control that opened the modal so focus can be restored on
    // close. Prefer the summary trigger when it (or something inside it) is the
    // active element at open time.
    this.opener = this.summaryToggle;

    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
    this.detailsContainer.setAttribute('open', true);
    document.body.addEventListener('click', this.onBodyClickEvent);

    this.summaryToggle.setAttribute('aria-expanded', 'true');
    HonHeaderOverlay.set('search', true);

    trapFocus(
      this.detailsContainer.querySelector('[tabindex="-1"]') || this.detailsContainer,
      this.detailsContainer.querySelector('input:not([type="hidden"])')
    );
  }

  close(focusToggle = true) {
    if (!this.isOpen()) return;

    removeTrapFocus(focusToggle ? this.opener || this.summaryToggle : null);
    this.detailsContainer.removeAttribute('open');
    document.body.removeEventListener('click', this.onBodyClickEvent);
    this.summaryToggle.setAttribute('aria-expanded', 'false');

    // Release only the search overlay's share of the header lock; the shared
    // `overflow-hidden` class owned by the cart drawer / other modals is left
    // untouched so those overlays stay locked.
    HonHeaderOverlay.set('search', false);
  }
}

customElements.define('details-modal', DetailsModal);
